import React, { useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  Plugin,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { shorten, TimeGranularity } from './StrategiesUtils';
import { IMassUnitObject } from '../../../sp-types/massUnitType';
import { GENERAL_ID } from '../../../components/Stores/exposureDataStore';
import { renderCoverageTooltipHTML } from './coverageTooltipUtils';
import { Account } from '../../../sp-types/accountTypes';
import { getSupplyTerm, getDemandTerm } from '../../../utils/categoryTerms';
import {
  renderBufferTooltip,
  renderLogisticsTooltip,
  renderCoverageTooltip,
  renderHedgesDerivingTooltip,
  renderBaseMaterialTooltip,
  renderDefaultTooltip,
} from './tooltipRenderers';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

// Global flag to control whether percentage labels are actually rendered.
// Temporarily hardcoded to false; when we want to enable coverage percentages,
// we can flip this to true without changing callers.
const SHOW_PERCENTAGE_LABELS = true;

export interface DatasetConfig {
  label: string;
  dataKey: string;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: number | { topLeft: number; topRight: number; bottomLeft: number; bottomRight: number };
  borderDash?: number[];
  type?: 'bar' | 'line';
  hidden?: boolean;
  fill?: boolean;
  pointRadius?: number;
  hideFromLegend?: boolean;
  stack?: string;
  order?: number;
  residualOf?: string[];   // dataKeys whose visible sum is subtracted from fullValueKey
  fullValueKey?: string;   // the raw full-value dataKey before residual subtraction
}

interface Props<T> {
  data: T[];
  matPrice: number;
  unit: IMassUnitObject;
  onTimeChange: (_time: TimeGranularity) => void;
  datasets: DatasetConfig[];
  pluginsFactory?: (_isQuantity: boolean, _matPrice: number) => Plugin[];
  stacked?: boolean;
  extraParams?: any;
  account?: Account;
  showPercentageLabels?: boolean;
  formulaId: string;
  onBarClick?: (_index: number) => void;
  selectedIndex?: number;
  initialGranularity?: TimeGranularity;
  renderControls?: (_params: {
    isQuantity: boolean;
    setIsQuantity: (_value: boolean) => void;
    timeGranularity: TimeGranularity;
    setTimeGranularity: (_value: TimeGranularity) => void;
    viewMonths: number;
    setViewMonths: (_value: number) => void;
    onShowCalc?: () => void;
  }) => React.ReactNode;
}

export function StrategyChart<T extends { month: string; [key: string]: any }>({
  data,
  matPrice,
  unit,
  onTimeChange,
  datasets,
  pluginsFactory,
  stacked = false,
  extraParams,
  account,
  showPercentageLabels = false,
  formulaId,
  onBarClick,
  selectedIndex,
  initialGranularity = 'Month',
  renderControls,
}: Props<T>) {
  const [isQuantity, setIsQuantity] = useState<boolean>(true);
  const [timeGranularity, setTimeGranularity] = useState<TimeGranularity>(initialGranularity);
  const [viewMonths, setViewMonths] = useState<number>(12);
  const chartRef = React.useRef<any>(null);
  const selectedIndexRef = React.useRef<number | undefined>(selectedIndex);
  const [animReady, setAnimReady] = useState(false);
  const displayDataRef = React.useRef<any[]>([]);
  const datasetsRef = React.useRef<DatasetConfig[]>(datasets);
  const multiplierRef = React.useRef(1);

  const timeGranRef = React.useRef(timeGranularity);
  React.useEffect(() => {
    if (timeGranRef.current !== timeGranularity) {
      timeGranRef.current = timeGranularity;
      onTimeChange(timeGranularity);
    }
  }, [timeGranularity]); // Remove onTimeChange from dependencies

  const chartKey = React.useMemo(() => {
    // Only include identity + display mode — NOT data values.
    // Data-dependent keys caused full remount (destroying animation).
    // Data changes should UPDATE the existing chart (animated transition).
    return `${formulaId}-${data.length}-${isQuantity}-${timeGranularity}-${viewMonths}`;
  }, [formulaId, data.length, isQuantity, timeGranularity, viewMonths]);

  // On every new chart mount (key change), start with zeros then flip to real data.
  // This forces Chart.js to animate from 0 → values on every mount.
  React.useEffect(() => {
    setAnimReady(false);
    const id = requestAnimationFrame(() => setAnimReady(true));
    return () => cancelAnimationFrame(id);
  }, [chartKey]);

  const displayData = useMemo(() => {
    // Full mode: show everything (on-hand + single aggregated bucket)
    if (timeGranularity === 'Full') return data;

    // For terminals, filter by absoluteMonth value; for months, filter by array index
    // Check if any item has absoluteMonth (not just first, since On-Hand doesn't have it)
    const hasAbsoluteMonth = data.some(item => item.absoluteMonth !== undefined);
    
    if (hasAbsoluteMonth) {
      // Terminal mode: keep On-Hand (no absoluteMonth) + terminals whose end falls within the horizon
      // absoluteMonth = months-from-now to terminal end; viewMonths = horizon in months
      return data.filter(item => item.absoluteMonth === undefined || item.absoluteMonth <= viewMonths);
    }
    // Month mode: filter by array index
    // If first item is an "On-Hand" entry, include it plus viewMonths of actual months
    const hasOnHand = data.length > 0 && data[0].month === 'On-Hand';
    return data.slice(0, viewMonths + (hasOnHand ? 1 : 0));
  }, [data, viewMonths, timeGranularity]);

  const enablePercentageLabels = showPercentageLabels && SHOW_PERCENTAGE_LABELS;

  // Selected bar indicator — reads from ref so it's always current
  const selectedBarPlugin: Plugin = useMemo(() => ({
    id: 'selectedBarIndicator',
    afterDraw(chart) {
      const idx = selectedIndexRef.current;
      if (idx == null || idx < 0) return;
      const { ctx, scales } = chart;
      const xScale = scales.x;
      if (!xScale) return;
      const x = xScale.getPixelForValue(idx);
      const y = chart.chartArea.bottom + 30;
      const size = 7;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x - size, y + size);
      ctx.lineTo(x + size, y + size);
      ctx.lineTo(x, y);
      ctx.closePath();
      ctx.fillStyle = 'rgba(59, 130, 246, 0.5)';
      ctx.fill();
      ctx.restore();
    },
  }), []); // stable — reads from ref

  // Update ref and trigger chart redraw when selectedIndex changes
  // Skip draw on mount — Chart.js initial animation handles it via the afterDraw plugin hook.
  // Calling draw() on mount renders the final frame instantly, killing the initial animation.
  const prevSelectedIndexRef = React.useRef(selectedIndex);
  React.useEffect(() => {
    selectedIndexRef.current = selectedIndex;
    if (prevSelectedIndexRef.current !== selectedIndex && chartRef.current) {
      chartRef.current.draw();
    }
    prevSelectedIndexRef.current = selectedIndex;
  }, [selectedIndex]);

  const plugins = useMemo(() => {
    const basePlugins = pluginsFactory ? pluginsFactory(isQuantity, matPrice) : [];

    // Add percentage label plugin if needed
    if (enablePercentageLabels) {
      const percentageLabelPlugin: Plugin = {
        id: 'percentageLabels',
        afterDatasetsDraw(chart) {
          const { ctx, data, scales } = chart;
          const xScale = scales.x;
          const yScale = scales.y;
          const currentDisplayData = displayDataRef.current;

          // Find the inventory/coverage bar dataset (first dataset)
          const inventoryDataset = data.datasets[1];
          const shortage = data.datasets[2];
          // Find the expected demand dataset (dashed line)
          const demandDataset = data.datasets[0]; //.find((ds) => ds.label?.includes('Expected'));
          const amountNeeded = data.datasets[3];

          if (!inventoryDataset || !demandDataset) return;

          // First supply dataset color = first material color (for physical dot)
          const firstSupplyDs = data.datasets.find((ds: any) => ds.label?.startsWith('Supply:'));
          const physicalDotColor = (firstSupplyDs?.backgroundColor as string) || '#6B7280';

          ctx.save();
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.font = 'bold 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

          inventoryDataset.data.forEach((value: any, index: number) => {
            // Skip on-hand bar (index 0)
            if (index === 0) return;
            
            const inventoryValue = value || 0;
            const demandValue = ((demandDataset.data[index] || shortage.data[index]) as number) || 0;
            const requiredAmount = (amountNeeded.data[index] as number) || 0;
            const originalRow = currentDisplayData[index];

            // Safety check: if originalRow is undefined, skip this iteration
            if (!originalRow) return;

            const rawPct = originalRow['covPercentage'] || 0;
            const percentage = (!isFinite(rawPct) || isNaN(rawPct)) ? 0 : rawPct;
            const rawHedgePct = originalRow['hedgedPercentage'] || 0;
            const hedgedPct = (!isFinite(rawHedgePct) || isNaN(rawHedgePct)) ? 0 : rawHedgePct;
            const x = xScale.getPixelForValue(index);
            const mult = isQuantity ? 1 : matPrice;
            
            // Use raw data values for FIXED label positioning (independent of series visibility)
            const rawPhysical = (originalRow['physicalCoverage'] || 0) * mult;
            const rawFinancial = (originalRow['financialPositions'] || 0) * mult;
            const rawFullDemand = (originalRow['fullDemand'] || 0) * mult;
            const totalStack = rawPhysical + rawFinancial + Math.max(0, rawFullDemand - rawPhysical - rawFinancial);
            const maxValue = Math.max(rawFullDemand, totalStack);
            const topY = yScale.getPixelForValue(maxValue);
            
            // Position label above the tallest element with generous spacing
            const hasHedge = hedgedPct > 0;
            const labelY = topY - (hasHedge ? 32 : 16);
            
            const totalPct = percentage + hedgedPct;
            
            // Row 1: total coverage % (bold, red/green)
            const mainText = `${totalPct}%`;
            ctx.font = 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
            const mainWidth = ctx.measureText(mainText).width;
            
            // Row 2: breakdown with colored dots — only if hedges exist
            const font11 = '11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
            ctx.font = font11;
            const physText = `${percentage}`;
            const hedgeText = `${hedgedPct}`;
            const slashText = ' + ';
            const dotR = 3.5; // dot radius
            const dotGap = 3; // gap between dot and number
            // Measure total width: ●gap70 / ●gap32
            const physW = ctx.measureText(physText).width;
            const hedgeW = ctx.measureText(hedgeText).width;
            const slashW = ctx.measureText(slashText).width;
            const breakdownTotalW = hasHedge ? (dotR*2 + dotGap + physW + slashW + dotR*2 + dotGap + hedgeW) : 0;
            const bgWidth = Math.max(mainWidth, breakdownTotalW);
            
            // White background covering both rows
            const bgHeight = hasHedge ? 32 : 18;
            ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.fillRect(x - bgWidth/2 - 4, labelY - 16, bgWidth + 8, bgHeight);
            
            // Draw row 1: total % (centered)
            ctx.font = 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
            ctx.fillStyle = totalPct >= 100 ? '#10B981' : '#EF4444';
            ctx.textAlign = 'center';
            ctx.fillText(mainText, x, labelY);
            
            // Draw row 2: ● 70 / ● 32 with colored dots
            if (hasHedge) {
              const row2Y = labelY + 14;
              let cx = x - breakdownTotalW / 2;
              // Green dot + physical %
              ctx.beginPath();
              ctx.arc(cx + dotR, row2Y - 5.5, dotR, 0, Math.PI * 2);
              ctx.fillStyle = physicalDotColor;
              ctx.fill();
              cx += dotR * 2 + dotGap;
              ctx.font = font11;
              ctx.fillStyle = '#6B7280';
              ctx.textAlign = 'left';
              ctx.fillText(physText, cx, row2Y);
              cx += physW;
              // Slash
              ctx.fillStyle = '#9CA3AF';
              ctx.fillText(slashText, cx, row2Y);
              cx += slashW;
              // Blue dot + hedge %
              ctx.beginPath();
              ctx.arc(cx + dotR, row2Y - 5.5, dotR, 0, Math.PI * 2);
              ctx.fillStyle = '#93C5FD';
              ctx.fill();
              cx += dotR * 2 + dotGap;
              ctx.fillStyle = '#6B7280';
              ctx.fillText(hedgeText, cx, row2Y);
            }
          });

          ctx.restore();
        },
      };

      return [...basePlugins, percentageLabelPlugin, selectedBarPlugin];
    }

    return [...basePlugins, selectedBarPlugin];
  }, [pluginsFactory, isQuantity, matPrice, enablePercentageLabels]);

  // Keep refs current for legend onClick callback
  React.useEffect(() => { displayDataRef.current = displayData; }, [displayData]);
  React.useEffect(() => { datasetsRef.current = datasets; }, [datasets]);
  React.useEffect(() => { multiplierRef.current = isQuantity ? 1 : matPrice; }, [isQuantity, matPrice]);

  /**
   * Generic residual recalculation.
   * For any dataset with `residualOf` + `fullValueKey`, recomputes its values as:
   *   max(0, fullValue[i] - sum(visible dependency values[i]))
   * Called after legend toggle to keep residual bars consistent with visible series.
   */
  const recalculateResiduals = React.useCallback((chart: any) => {
    const dsCfg = datasetsRef.current;
    const data = displayDataRef.current;
    const mult = multiplierRef.current;

    dsCfg.forEach((cfg, dsIndex) => {
      if (!cfg.residualOf || !cfg.fullValueKey) return;

      const chartDs = chart.data.datasets[dsIndex];
      if (!chartDs) return;

      // Determine which dependency datasets are currently visible
      const visibleDepKeys = cfg.residualOf.filter(depKey => {
        const depIdx = dsCfg.findIndex(d => d.dataKey === depKey);
        if (depIdx < 0) return false;
        const depMeta = chart.getDatasetMeta(depIdx);
        return !depMeta.hidden;
      });

      // Recalculate residual: fullValue - sum(visible deps)
      chartDs.data = data.map((item: any) => {
        const fullVal = (item[cfg.fullValueKey!] ?? 0) * mult;
        let visibleSum = 0;
        for (const depKey of visibleDepKeys) {
          visibleSum += Math.max(0, (item[depKey] ?? 0) * mult);
        }
        return Math.max(0, fullVal - visibleSum);
      });
    });
  }, []);

  const chartData = useMemo(() => {
    const labels = displayData.map((item) => item.month);
    const multiplier = isQuantity ? 1 : matPrice;

    return {
      labels,
      datasets: datasets.map((dataset) => {
        const rawData = displayData.map((item) => {
          const value = item[dataset.dataKey];
          if (value === null || value === undefined) return null;
          const scaled = value * multiplier;
          // Clamp bar values to >= 0 to prevent negative rendering (e.g. on-hand)
          return dataset.type === 'line' ? scaled : Math.max(0, scaled);
        });

        return {
          label: dataset.label,
          data: rawData,
          backgroundColor: dataset.backgroundColor || '#93C5FD',
          borderColor: dataset.borderColor || dataset.backgroundColor || '#3B82F6',
          borderWidth: dataset.borderWidth ?? (dataset.type === 'line' ? 2 : 0),
          borderRadius: dataset.borderRadius ?? 0,
          borderSkipped: 'bottom',
          borderDash: dataset.borderDash,
          type: dataset.type || 'bar',
          hidden: dataset.hidden,
          hideFromLegend: dataset.hideFromLegend, // This controls legend visibility
          fill: dataset.fill ?? false,
          pointRadius: dataset.pointRadius ?? (dataset.type === 'line' ? 0 : undefined),
          ...(dataset.stack ? { stack: dataset.stack } : {}),
          ...(dataset.order != null ? { order: dataset.order } : {}),
        };
      }),
    };
  }, [displayData, isQuantity, matPrice, datasets, stacked, extraParams, account, formulaId, showPercentageLabels, onBarClick]);

  // Compute a fixed y-axis max from ALL data so scale doesn't shift when toggling series
  const fixedYMax = useMemo(() => {
    const multiplier = isQuantity ? 1 : matPrice;
    const barDatasets = datasets.filter(d => d.type !== 'line');
    // Group bar datasets by stack name (default: '__default')
    const stackGroups: Record<string, DatasetConfig[]> = {};
    for (const ds of barDatasets) {
      const key = ds.stack || '__default';
      if (!stackGroups[key]) stackGroups[key] = [];
      stackGroups[key].push(ds);
    }
    let maxVal = 0;
    for (let i = 0; i < displayData.length; i++) {
      // For each index, compute each stack group's total and take the max across groups
      for (const group of Object.values(stackGroups)) {
        let stackTotal = 0;
        for (const ds of group) {
          const v = displayData[i]?.[ds.dataKey];
          if (v != null) stackTotal += Math.max(0, v * multiplier);
        }
        if (stackTotal > maxVal) maxVal = stackTotal;
      }
    }
    // Also consider line datasets
    for (const ds of datasets.filter(d => d.type === 'line')) {
      for (let i = 0; i < displayData.length; i++) {
        const v = displayData[i]?.[ds.dataKey];
        if (v != null && v * multiplier > maxVal) maxVal = v * multiplier;
      }
    }
    return maxVal * 1.15; // 15% headroom
  }, [displayData, datasets, isQuantity, matPrice]);

  const options: ChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 400,
      },
      elements: {
        point: {
          radius: 0,
          hoverRadius: 0,
          hitRadius: 0,
        },
      },
      barPercentage: 0.5,
      categoryPercentage: 0.8,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      onClick: onBarClick ? (_event: any, elements: any[]) => {
        if (elements.length > 0) {
          const index = elements[0].index;
          onBarClick(index);
        }
      } : undefined,
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: {
            padding: 20,
            font: {
              size: 12,
            },
            filter: (item, data) => {
              // Hide dataset from legend if hideFromLegend is true
              const dataset = data.datasets[item?.datasetIndex || 0] as any;
              return !(dataset as any & { hideFromLegend: boolean }).hideFromLegend;
            },
          },
          onClick: (_e: any, legendItem: any, legend: any) => {
            const chart = legend.chart;
            const dsIndex = legendItem.datasetIndex;
            const meta = chart.getDatasetMeta(dsIndex);
            // Toggle visibility (default Chart.js behavior)
            meta.hidden = meta.hidden === null ? !chart.data.datasets[dsIndex].hidden : null;
            // Recalculate any residual datasets based on new visibility
            recalculateResiduals(chart);
            chart.update();
          },
        },
        tooltip: {
          enabled: false,
          external: (context) => {
            // Tooltip Element
            let tooltipEl = document.getElementById('chartjs-tooltip');

            // Create element on first render
            if (!tooltipEl) {
              tooltipEl = document.createElement('div');
              tooltipEl.id = 'chartjs-tooltip';
              tooltipEl.style.background = 'white';
              tooltipEl.style.borderRadius = '8px';
              tooltipEl.style.boxShadow = '0 0 16px rgba(0, 0, 0, 0.12), 0 0 4px rgba(0, 0, 0, 0.08)';
              tooltipEl.style.opacity = '1';
              tooltipEl.style.pointerEvents = 'none';
              tooltipEl.style.position = 'absolute';
              tooltipEl.style.transform = 'translate(-50%, 0)';
              tooltipEl.style.transition = 'all .1s ease';
              tooltipEl.style.padding = '12px';
              tooltipEl.style.minWidth = '200px';
              document.body.appendChild(tooltipEl);
              
              // Add triangle pointer at top
              const triangle = document.createElement('div');
              triangle.id = 'tooltip-triangle';
              triangle.style.position = 'absolute';
              triangle.style.top = '-8px';
              triangle.style.left = '50%';
              triangle.style.transform = 'translateX(-50%)';
              triangle.style.width = '0';
              triangle.style.height = '0';
              triangle.style.borderLeft = '8px solid transparent';
              triangle.style.borderRight = '8px solid transparent';
              triangle.style.borderBottom = '8px solid white';
              triangle.style.filter = 'drop-shadow(0 -3px 3px rgba(0, 0, 0, 0.12))';
              tooltipEl.appendChild(triangle);
            }

            // Hide if no tooltip
            const tooltipModel = context.tooltip;
            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = '0';
              return;
            }

            // Set Text
            if (tooltipModel.body) {
              const dataPoints = tooltipModel.dataPoints;
              const { dataIndex } = dataPoints[0];
              const originalRow = displayData[dataIndex];

              // Get custom terms
              const supplyTerm = getSupplyTerm(account);
              const demandTerm = getDemandTerm(account);

              // Dispatch to the correct per-policy tooltip renderer
              let result;
              if (extraParams?.bufferRows && extraParams.bufferRows.length > 0) {
                result = renderBufferTooltip(dataIndex, extraParams.bufferRows, isQuantity, matPrice, supplyTerm, demandTerm);
              } else if (extraParams?.coverage) {
                result = renderCoverageTooltip(dataIndex, originalRow, isQuantity, matPrice, supplyTerm, demandTerm);
              } else if (extraParams?.logistics) {
                result = renderLogisticsTooltip(dataIndex, originalRow, isQuantity, matPrice, supplyTerm, demandTerm);
              } else if (extraParams?.hedgesDeriving && extraParams?.derivedMaterials) {
                result = renderHedgesDerivingTooltip(originalRow, extraParams.derivedMaterials, datasets, isQuantity);
              } else if (extraParams?.baseMaterial) {
                result = renderBaseMaterialTooltip(dataIndex, originalRow, datasets, isQuantity, matPrice);
              } else {
                result = renderDefaultTooltip(tooltipModel, isQuantity);
              }

              tooltipEl.innerHTML = renderCoverageTooltipHTML({
                values: result.values,
                labels: result.labels,
                colors: result.colors,
                formulas: result.formulas,
                subtitles: result.subtitles,
                footer: result.footer,
                separators: result.separators,
                html: result.html,
              });

              // Re-append triangle (innerHTML wipes it)
              let tri = tooltipEl.querySelector('#tooltip-triangle') as HTMLElement;
              if (!tri) {
                tri = document.createElement('div');
                tri.id = 'tooltip-triangle';
                tri.style.cssText = 'position:absolute;top:-8px;left:50%;transform:translateX(-50%);width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-bottom:8px solid white;filter:drop-shadow(0 -3px 3px rgba(0,0,0,0.12))';
                tooltipEl.appendChild(tri);
              }
            }

            const position = context.chart.canvas.getBoundingClientRect();
            const chartArea = context.chart.chartArea;

            // Position tooltip below the chart area, month labels, and selected-bar marker
            tooltipEl.style.opacity = '1';
            tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
            tooltipEl.style.top = position.top + window.pageYOffset + chartArea.bottom + 50 + 'px';
          },
          intersect: false,
        },
      },
      scales: {
        x: {
          stacked,
          grid: {
            display: true,
            color: '#F3F4F6',
            lineWidth: 1,
          },
          ticks: {
            font: (ctx: any) => ({
              size: 11,
              weight: ctx.index === selectedIndexRef.current ? 'bold' as const : 'normal' as const,
            }),
            color: (ctx: any) =>
              ctx.index === selectedIndexRef.current ? 'rgba(59, 130, 246, 0.8)' : '#6B7280',
          },
        },
        y: {
          stacked,
          beginAtZero: true,
          min: 0,
          suggestedMax: fixedYMax || undefined,
          grid: {
            color: (context) => {
              return context.tick.value === 0 ? '#374151' : '#F3F4F6';
            },
            lineWidth: 1,
          },
          ticks: {
            callback: (value) => shorten(+value),
            font: {
              size: 11,
            },
            color: '#6B7280',
          },
          title: {
            display: true,
            text: isQuantity ? unit?.displayName || 'mt' : 'USD $',
            color: '#374151',
            font: {
              size: 12,
              weight: 'bold',
            },
          },
        },
      },
    }),
    [isQuantity, unit, stacked, showPercentageLabels, matPrice, data, displayData, extraParams, viewMonths, fixedYMax, recalculateResiduals],
  );


  if (formulaId === GENERAL_ID) {
    return (
      <div className="flex flex-col bg-white shadow-md rounded-lg p-2 h-full">
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-500 text-3xl">Select material</div>
        </div>
      </div>
    );
  }
  if (!data.length) {
    return (
      <div className="flex flex-col bg-white shadow-md rounded-lg p-2 h-full">
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-500">No data available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white shadow-md rounded-lg p-2 h-full">
      {renderControls && renderControls({
        isQuantity,
        setIsQuantity,
        timeGranularity,
        setTimeGranularity,
        viewMonths,
        setViewMonths,
      })}
      <div className="flex-1 overflow-hidden">
        <Chart ref={chartRef} key={chartKey} type="bar" data={animReady ? chartData : { ...chartData, datasets: chartData.datasets.map(ds => ({ ...ds, data: ds.data.map(() => 0) })) }} options={options} plugins={plugins} />
      </div>
    </div>
  );
}
